import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useSeasonContext } from '../context/SeasonContext';
import { formatTimeWithTimezone } from '../utils/timezoneUtils';
import '../pages/Pages.css';
import '../styles/Schedule.css';

function Schedule() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState('all');
  const { selectedSeason } = useSeasonContext();
  const [weeks, setWeeks] = useState([]);
  const [teams, setTeams] = useState({});
  const [standings, setStandings] = useState({});

  useEffect(() => {
    if (selectedSeason) {
      fetchSchedule(selectedSeason.id);
    }
  }, [selectedSeason]);

  const fetchSchedule = async (seasonId) => {
    try {
      setLoading(true);
      setError(null);

      const { data: teamStandings, error: standingsError } = await supabase
        .from('team_standings')
        .select('team_id, rank, wins, losses, teams(id, name, abbreviation, logo_url)')
        .eq('season_id', seasonId)
        .order('rank', { ascending: true });

      if (standingsError) throw standingsError;

      const teamMap = {};
      const standingsMap = {};
      if (teamStandings && Array.isArray(teamStandings)) {
        teamStandings.forEach((standing) => {
          if (standing.teams) {
            teamMap[standing.team_id] = {
              name: standing.teams.name || `Team ${standing.team_id}`,
              logo: standing.teams.logo_url
            };
            standingsMap[standing.team_id] = {
              ranking: standing.rank || '-',
              wins: standing.wins || 0,
              losses: standing.losses || 0
            };
          }
        });
      }
      setTeams(teamMap);
      setStandings(standingsMap);

      const { data, error: fetchError } = await supabase
        .from('games')
        .select('id, week, scheduled_date, is_completed, team1_id, team2_id')
        .eq('season_id', seasonId)
        .order('week', { ascending: true })
        .order('id', { ascending: true });

      if (fetchError) throw fetchError;
      
      const gamesList = data || [];
      
      if (gamesList.length > 0) {
        setGames(gamesList);
        const uniqueWeeks = [...new Set(gamesList.map(game => game.week))].sort((a, b) => a - b);
        setWeeks(uniqueWeeks);
      } else {
        setGames([]);
        setWeeks([]);
      }
      setSelectedWeek('all');
    } catch (err) {
      setError('Failed to load schedule');
      console.error('Schedule fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredGames = () => {
    if (selectedWeek === 'all') {
      return games;
    }
    return games.filter(game => game.week === parseInt(selectedWeek));
  };

  const getTeamName = (teamId) => {
    return teams[teamId]?.name || 'Unknown';
  };

  const getTeamLogo = (teamId) => {
    return teams[teamId]?.logo;
  };

  const getTeamRanking = (teamId) => {
    return standings[teamId]?.ranking || '-';
  };

  const getTeamRecord = (teamId) => {
    const record = standings[teamId];
    if (!record) return '0-0';
    return `${record.wins}-${record.losses}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return formatTimeWithTimezone(dateString);
  };

  if (loading) {
    return (
      <div className="page">
        <h1>Schedule</h1>
        <p>Loading schedule...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <h1>Schedule</h1>
        <p style={{ color: 'red' }}>Error: {error}</p>
      </div>
    );
  }

  const filteredGames = getFilteredGames();

  return (
    <div className="page schedule-page">
      <h1>Schedule</h1>
      
      {games.length === 0 ? (
        <p>No scheduled games available at this time.</p>
      ) : (
        <>
          <div className="schedule-filters">
            <label htmlFor="week-select">Week: </label>
            <select
              id="week-select"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              className="week-select"
            >
              <option value="all">All Weeks</option>
              {weeks.map(week => (
                <option key={week} value={week}>
                  Week {week}
                </option>
              ))}
            </select>
          </div>

          {filteredGames.length === 0 ? (
            <p>No games scheduled for the selected week.</p>
          ) : (
            <div className="schedule-list">
              {filteredGames.map((game) => (
                <div key={game.id} className="schedule-card">
                  <div className="schedule-week">Week {game.week}</div>
                  <div className="schedule-matchup">
                    <div className="team team-1">
                      <div className="team-header">
                        <span className="team-ranking">#{getTeamRanking(game.team1_id)}</span>
                        {getTeamLogo(game.team1_id) && (
                          <img src={getTeamLogo(game.team1_id)} alt={getTeamName(game.team1_id)} className="team-logo" />
                        )}
                      </div>
                      <span className="team-name">{getTeamName(game.team1_id)}</span>
                      <span className="team-record">{getTeamRecord(game.team1_id)}</span>
                    </div>
                    <div className="vs">vs</div>
                    <div className="team team-2">
                      <div className="team-header">
                        <span className="team-ranking">#{getTeamRanking(game.team2_id)}</span>
                        {getTeamLogo(game.team2_id) && (
                          <img src={getTeamLogo(game.team2_id)} alt={getTeamName(game.team2_id)} className="team-logo" />
                        )}
                      </div>
                      <span className="team-name">{getTeamName(game.team2_id)}</span>
                      <span className="team-record">{getTeamRecord(game.team2_id)}</span>
                    </div>
                  </div>
                  <div className="schedule-details">
                    <div className="game-date">
                      {formatDate(game.scheduled_date)}
                    </div>
                    <div className="game-time">
                      {formatTime(game.scheduled_date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Schedule;
